import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type {
  ChallengeItemSectionItem,
  ChallengeSectionItem,
  CollectionSectionItem,
} from "@packages/sdk";
import { isBefore } from "date-fns/isBefore";

import { ChallengeCTAButton } from "../../../../blocks/primitives/refactor/ChallengeCTAButton/ChallengeCTAButton.emotion";
import { Button } from "../../../_core/Button/Button.emotion";
import type { ChallengeLargeProps } from "../../challenges/ChallengeLarge/ChallengeLarge.emotion";
import type { BaseGetSectionItemProps } from "./Section.emotion";

/**
 * TODO: JoinButton
 * TODO: MediaPlayer
 */
export type GetSectionItemChallengePropsProps = BaseGetSectionItemProps<
  ChallengeSectionItem | ChallengeItemSectionItem | CollectionSectionItem
>;

export const getSectionItemChallengeProps = ({
  item,
  link,
  mutate,
  t,
  analytics,
}: GetSectionItemChallengePropsProps): ChallengeLargeProps => {
  const collectionData =
    item.reference_type === "challenge" || item.reference_type === "collection"
      ? item.reference
      : item.reference.collection;

  const actions = [
    mutate ? (
      <ChallengeCTAButton
        collection={collectionData}
        challengeId={item.reference_id}
        sectionId={item.item_id}
        key="cta"
      />
    ) : null,
    link ? (
      <Button
        asChild
        key="link"
        variant="lighten"
        onClickCapture={() => {
          analytics.track({
            event: HallowAnalyticsEvent.TappedDetails,
            properties: {
              section_item_id: item?.item_id ?? null,
              collection_id: collectionData.id,
            },
          });
        }}
      >
        <Link href={`/collections/${collectionData.id}`}>
          {t("web_details")}
        </Link>
      </Button>
    ) : null,
  ];

  const centerActions = [
    mutate ? (
      <ChallengeCTAButton
        collection={collectionData}
        challengeId={item.reference_id}
        sectionId={item.item_id}
        key="cta"
      />
    ) : null,
  ];

  let usersTag: string | null = null;
  if (collectionData.type === "challenge") {
    const fmt = Intl.NumberFormat(undefined, {
      useGrouping: true,
      maximumFractionDigits: 0,
    });
    if (collectionData.users_count) {
      if (collectionData.place_in_line) {
        const placeInLine = Math.min(
          collectionData.place_in_line,
          collectionData.users_count,
        );
        const props = {
          "0": fmt.format(placeInLine),
          "1": fmt.format(collectionData.users_count),
        };
        if (isBefore(new Date(collectionData.available_at), new Date())) {
          usersTag = t("challenge_tile_of_praying", props);
        } else {
          usersTag = t("challenge_tile_of_joined", props);
        }
      } else {
        usersTag = t("challenge_tile_praying", {
          "0": fmt.format(collectionData.users_count),
        });
      }
    }
  }

  return {
    actions,
    centerActions,
    asChild: link,
    availableAt:
      collectionData.type === "challenge" ? collectionData.available_at : null,
    children: link ? <Link href={`/collections/${collectionData.id}`} /> : null,
    color: collectionData.images?.color_hex as ChallengeLargeProps["color"],
    description: collectionData?.short_desc ?? collectionData?.desc,
    endsAt: collectionData?.ends_at,
    imgBlur:
      collectionData.type === "challenge" ? !collectionData.has_joined : false,
    imgSrc: collectionData?.images,
    subscription: !collectionData?.has_access,
    title: collectionData?.title,
    tags: usersTag ? [usersTag] : [],
  };
};
